import {
  Card,
  Container,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import React from 'react';

import Iconify from '../../components/Iconify';
import useToggle from '../../hooks/useToggle';

const RootStyle = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(5),
  paddingBottom: theme.spacing(10),
  [theme.breakpoints.down('md')]: {
    paddingTop: theme.spacing(10),
    paddingBottom: theme.spacing(5),
  },
}));

const CardStyle = styled(Card)(({ theme, url }) => ({
  border: 0,
  width: '100%',
  minHeight: 360,
  maxHeight: 360,
  margin: 'auto',
  position: 'relative',
  backgroundImage: `url(/assets/clearoverlay.svg), url(${url})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  outline: '2px solid transparent',
  outlineOffset: -2,
  transition: 'box-shadow 0.2s ease, transform 0.2s ease, outline-color 0.2s ease',
  '&:hover': {
    boxShadow: '0 12px 28px rgba(0, 0, 0, 0.2)',
    transform: 'translateY(-2px)',
    outlineColor: theme.palette.primary.main,
  },
}));

const RAISONS = [
  {
    image: '/assets/background/AdobeStock_305621580.jpg',
    title: 'Un port en eau profonde',
    desc: 'Le Port autonome de Lomé a traité 30,6 millions de tonnes de marchandises et 2,06 millions dʼEVP en 2024. Son tirant dʼeau annoncé de 16,60 m permet lʼaccueil de grands navires et son positionnement dessert les marchés côtiers et sahéliens.',
  },
  {
    image: '/assets/background/Salt-Lomé-Togo.jpg',
    title: 'Un hub aérien performant',
    desc: 'Lomé constitue un hub aérien régional. Vingt-huit villes internationales sont reliées depuis Lomé, notamment New York, Bruxelles, Paris et Addis-Abeba. Les destinations et les fréquences pouvant évoluer, consultez les horaires des compagnies aériennes.',
  },
  {
    image: '/assets/background/image_4.jpg',
    title: "Une porte d'entrée stratégique",
    desc: 'Le Togo se situe sur le corridor côtier Abidjan-Lagos et offre des liaisons vers le Burkina Faso, le Mali et le Niger. Depuis le Togo, les entreprises peuvent cibler lʼUEMOA, la CEDEAO — marché de plus de 400 millions dʼhabitants — et la ZLECAf, sous réserve des règles dʼorigine et des conditions applicables.',
  },
  {
    image: '/assets/background/istockphoto-1171807822.jpg',
    title: 'Un climat des affaires attractif',
    desc: 'Le Togo est classé 1er en Afrique de l’Ouest et parmi les meilleurs en Afrique subsaharienne dans le rapport Business Ready (B-READY) 2025 de la Banque mondiale.',
  },
  {
    image: '/assets/background/AdobeStock_191995730.jpg',
    title: 'Un accès privilégié au marché mondial',
    desc: 'Les entreprises établies au Togo peuvent examiner les régimes de la CEDEAO, de l’UEMOA et de la ZLECAf, ainsi que les dispositifs AGOA et « Tout sauf les armes », sous réserve des règles d’origine, des agréments et de leur validité au moment de l’exportation.',
  },

  {
    image: '/assets/background/photo-lawyers.jpg',
    title: 'Un cadre juridique et fiscal attractif',
    desc: 'Le Code des investissements s’applique aux projets nouveaux ou d’extension atteignant au moins 50 millions FCFA. Le statut de la Zone franche vise les entreprises qui produisent des biens ou des services et exportent au moins 70 % de leur production. Les avantages dépendent de l’instruction du dossier et de l’agrément.',
  },
  {
    image: '/assets/background/AdobeStock_411430356.jpg',
    title: 'Une main-d’œuvre jeune',
    desc: 'Le Togo dispose d’une population jeune et d’établissements de formation. Les entreprises peuvent présenter les compétences recherchées, les partenariats de formation et les dispositifs d’emploi accessibles pour accompagner leurs projets.',
  },
  {
    image: '/assets/background/AdobeStock_137648639.jpg',
    title: 'Une place financière régionale',
    desc: 'Lomé accueille des institutions financières régionales et des groupes bancaires panafricains, formant un écosystème financier à dimension régionale.',
  },
  {
    image: '/assets/background/AdobeStock_144613163.jpg',
    title: 'Une stratégie publique tournée vers la transformation',
    desc: 'Les priorités publiques mettent l’accent sur l’investissement privé, l’amélioration du climat des affaires et la transformation numérique. Elles seront reliées au document gouvernemental officiel dès sa publication.',
  },
  {
    image: '/assets/background/pays_paix.jpg',
    title: 'Un environnement propice aux projets de long terme',
    desc: 'Le Togo propose un cadre de coopération pour les investissements de long terme. La protection des investissements, la prévention des risques, la sécurité des opérations et le règlement des différends constituent des dimensions essentielles de la préparation des projets.',
  },
];

export default function HomeRaison() {
  return (
    <Stack width={1} position="relative">
      <RootStyle>
        <Grid container spacing={{ xs: 2, sm: 5, md: 8 }} justifyContent="center">
          {RAISONS.map((raison, index) => (
            <Grid key={raison.title} item xs={12} md={4}>
              <RaisonCard
                number={String(index + 1).padStart(2, '0')}
                title={raison.title}
                desc={raison.desc}
                image={raison.image}
              />
            </Grid>
          ))}
        </Grid>
      </RootStyle>
    </Stack>
  );
}

const RaisonCard = ({ number, title, desc, image }) => {
  const { toggle, onOpen, onClose } = useToggle();

  return (
    <CardStyle url={image}>
      <Stack
        onClick={onOpen}
        height={360}
        justifyContent="center"
        sx={{ cursor: 'pointer', backgroundColor: 'rgba(0, 0, 0, 0.18)' }}
      >
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            minWidth: 48,
            px: 1.5,
            py: 1,
            color: 'grey.900',
            textAlign: 'center',
            bgcolor: 'warning.main',
            borderRadius: 1,
          }}
        >
          {number}
        </Typography>

        <Stack
          justifyContent="center"
          minHeight={120}
          px={3}
          py={2.5}
          sx={{ backgroundColor: 'rgba(0, 0, 0, 0.68)' }}
        >
          <Typography variant="h4" color="common.white" fontWeight="bold" textAlign="center">
            {title}
          </Typography>
        </Stack>
      </Stack>
      {toggle && (
        <Dialog open={toggle} onClose={onClose}>
          <DialogTitle>
            <Stack direction="row" justifyContent="space-between" alignItems="start" pb={3}>
              <Typography variant="h4">{title}</Typography>
              <IconButton onClick={onClose}>
                <Iconify icon="eva:close-fill" />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <DialogContentText textAlign="justify"> {desc} </DialogContentText>
          </DialogContent>
        </Dialog>
      )}
    </CardStyle>
  );
};
